import { CalendarIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MonthGrid } from '@/components/layout/MonthGrid'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

/**
 * The two ends of a period, shared by the two fields that show them.
 *
 * Both fields hand the same object down, so either calendar picks the whole period: one click for
 * the first day, one for the last, and the second field fills itself in. A fair is entered as "the
 * week of the 10th", not as two dates that happen to be four days apart.
 */
export type DateRange = {
  /** The first day, as an ISO date, or empty. */
  start: string
  /** The last day. Empty while a period is half-picked. */
  end: string
  /** Which end of the period *this* field shows — what its calendar's first click moves. */
  side: 'start' | 'end'
  onChange: (start: string, end: string) => void
}

/**
 * A date, typed or picked.
 *
 * The control underneath stays a native `<input type="date">`: it keeps the ISO value, the
 * keyboard entry everybody at a desk is faster with, and the operating system's own picker on a
 * phone. The calendar button is what the browsers disagree about — one hides its picker behind a
 * tiny glyph, another opens nothing at all — so the button is ours and looks the same everywhere.
 * The browser's own indicator is hidden where it can be, rather than sitting next to ours.
 *
 * Firefox is where it cannot be: its calendar button answers to no pseudo-element and no
 * `appearance`. So there the field makes room for nothing, the browser's glyph moves to the right
 * edge, and our button lies over it — drawing no icon and no background of its own, so the one
 * calendar showing is Firefox's, and a click on it still opens ours. `-moz-appearance` is what
 * tells Firefox apart; no other engine supports it. Only with a mouse: Firefox on a phone draws no
 * glyph and opens its own picker on a tap, so there our icon is the only one. Should Firefox ever
 * drop `-moz-appearance`, the rule simply stops applying and the two icons are back — nothing
 * breaks. No hover background there either: it would paint over the glyph it is meant to frame.
 *
 * With [range] the calendar picks a period across two of these fields; without it, one day.
 */
export function DateField({
  id,
  value,
  onChange,
  invalid,
  describedBy,
  min,
  calendarLabel,
  range,
}: {
  id: string
  /** An ISO date, or an empty string while the field is empty or half-typed. */
  value: string
  onChange: (value: string) => void
  invalid?: boolean
  describedBy?: string
  /** The earliest date the input itself accepts. The calendar is not bound by it. */
  min?: string
  /** Said instead of "open the calendar", where two of these sit side by side. */
  calendarLabel?: string
  /** Set on both fields of a period, and by the same object. */
  range?: DateRange
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  /**
   * Whether *this* calendar is between the two clicks of a period. Held here rather than read off
   * an empty end date, so the field a calendar was opened from still decides what its first click
   * does — opening the start field's calendar on a period whose end is missing moves the start,
   * which is what the button next to "Beginn" promises.
   */
  const [awaitingEnd, setAwaitingEnd] = useState(false)

  function select(picked: string) {
    if (!range) {
      onChange(picked)
      setOpen(false)
      return
    }
    if (awaitingEnd) {
      // Picked backwards, which is a beginning rather than a refusal: the period simply starts
      // there instead, and the calendar waits for its end again.
      if (picked < range.start) {
        range.onChange(picked, '')
        return
      }
      range.onChange(range.start, picked)
      setOpen(false)
      return
    }
    if (range.side === 'end' && range.start !== '' && picked >= range.start) {
      // The end alone, from the field that shows it. The beginning stays where it was.
      range.onChange(range.start, picked)
      setOpen(false)
      return
    }
    // A new period begins here. An end still after it survives — shortening a fair by a day
    // should not cost the other date — and anything else waits for the second click.
    const keptEnd = range.end && range.end >= picked ? range.end : ''
    range.onChange(picked, keptEnd)
    if (keptEnd) {
      setOpen(false)
      return
    }
    setAwaitingEnd(true)
  }

  // What the next click will do, for a calendar that is staying open on a half-picked period.
  const nextIsEnd = awaitingEnd || (range?.side === 'end' && range.start !== '')

  return (
    <div className="relative">
      <Input
        id={id}
        type="date"
        value={value}
        min={min}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        className="pr-9 supports-[-moz-appearance:none]:pointer-fine:pr-1 [&::-webkit-calendar-picker-indicator]:hidden"
        onChange={(event) => onChange(event.target.value)}
      />
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          // Closing halfway through leaves the period as it stands; reopening starts over.
          setAwaitingEnd(false)
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="absolute top-0.5 right-0.5 supports-[-moz-appearance:none]:pointer-fine:bg-transparent! supports-[-moz-appearance:none]:pointer-fine:[&_svg]:invisible"
            aria-label={calendarLabel ?? t('common.openCalendar')}
          >
            <CalendarIcon />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-auto">
          <MonthGrid
            // The day in this field, failing that the one the period starts on, failing that today.
            focusOn={value || range?.start || ''}
            start={range ? range.start : value}
            end={range ? range.end : value}
            caption={range && (nextIsEnd ? t('common.pickEndDate') : t('common.pickStartDate'))}
            onSelect={select}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}
